import createAuthenticationLogger from "./authentication";
import createAssessmentLogger from "./assessment";
import createBrowserLogger from "./browser";
import { createBasicLoggerStateStore } from "./LoggerStateStore";
export function createProxyVerityLogger({ logFunction, getDynamicContext }) {
    let pendingRequests = [];
    const log = async (log) => {
        const promise = logFunction({
            ...log,
            context: log.context ?? getDynamicContext?.(),
            time: log.time ?? new Date(),
        });
        pendingRequests.push(promise);
        promise?.finally(() => {
            pendingRequests = pendingRequests.filter((p) => p !== promise);
        });
        return promise;
    };
    const error = (error) => {
        console.error("creating log for error", error);
        return log({
            type: "error",
            details: {
                error: String(error),
                stack: error && typeof error === "object" && "stack" in error ? error.stack : undefined,
            },
        });
    };
    return {
        log,
        error,
        async capturePromiseError(promise) {
            try {
                return await promise;
            }
            catch (e) {
                error(e);
                throw e;
            }
        },
        authentication: createAuthenticationLogger(log),
        assessment: createAssessmentLogger(log),
        browser: createBrowserLogger(log),
        async dispose() {
            await Promise.allSettled(pendingRequests);
        },
    };
}
export default function createVerityLogger({ platform, baseUrl, fetch, stateStore = createBasicLoggerStateStore(), getDynamicContext, }) {
    const createSession = async (input) => {
        try {
            const response = await fetch(`${baseUrl}/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(input),
            });
            if (response.ok) {
                return response.json();
            }
            else {
                console.error("session creation failed", response.status, input);
            }
        }
        catch (error) {
            console.error(error);
        }
    };
    const createLog = async (sessionId, log) => {
        try {
            const response = await fetch(`${baseUrl}/${sessionId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(log),
            });
            if (!response.ok) {
                console.error("logging failed", response.status, sessionId, log);
            }
        }
        catch (error) {
            console.error(error);
        }
    };
    let updatedState = Promise.resolve(stateStore.getState());
    const getUpdatedState = () => {
        updatedState = updatedState.then(async (prevState) => {
            try {
                let { session, context } = await stateStore.getState();
                if (!session ||
                    context.userId !== (session.userId || undefined) ||
                    (!session.installationId && context.installationId) ||
                    (!session.fingerprint && context.fingerprint) ||
                    (!session.systemDetails && context.systemDetails) ||
                    (!session.platformDetails && context.platformDetails)) {
                    session = await createSession({ ...context, platform });
                    await stateStore.setState((context) => ({ ...context, session }));
                }
                return { session, context };
            }
            catch (e) {
                console.error(e);
                return prevState;
            }
        });
        return updatedState;
    };
    return {
        ...createProxyVerityLogger({
            logFunction: async (log) => {
                const time = log.time ?? new Date();
                const { session, context } = await getUpdatedState();
                if (session) {
                    await createLog(session.id, {
                        proctoringSessionId: context.proctoringSessionId,
                        liveSessionId: context.liveSessionId,
                        ...log,
                        time,
                    });
                }
            },
            getDynamicContext,
        }),
        stateStore,
    };
}
export { createBasicLoggerStateStore };
